import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import {
  ApiResListsType,
  ApiResponseType,
  OhsApiRequestName,
} from 'global-services/api/OhsApiModels';
import {
  getTaskLists,
  getBookmarkListByTask,
  getSignedOffTaskList,
  getSignedOffTaskSearchList,
  getTaskSearchLists,
} from 'task/OhsTaskServices';
import {
  FetchTaskListTypes,
  OhsTaskListsFilterPayload,
  OhsTaskSearchMain,
  TaskListsRecord,
} from 'task/OhsTaskTypes';
import globalModuleSearch from 'search/OhsSearchServices';
import { OhsTabKeyTypes } from 'global-components/tab/OhsTabModels';
import { ModuleType } from 'global-services/constants/OhsObject';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { OhsUser } from 'user/OhsUser';

const defaultTaskLists = {
  items: [],
  pagination: {
    page: 0,
    totalPages: 0,
    next: '',
  },
};

export interface TaskListsRecordState {
  isLoading: boolean;
  taskLists: ApiResListsType<TaskListsRecord[]> | null;
  flaggedTaskLists: ApiResListsType<TaskListsRecord[]> | null;
  flaggedTaskListsByID: ApiResponseType<any> | null;
  currentPage: number;
}

const initialState: TaskListsRecordState = {
  isLoading: true,
  taskLists: defaultTaskLists,
  flaggedTaskLists: null,
  flaggedTaskListsByID: null,
  currentPage: 1,
};

export const defaultTaskList = {
  items: [],
  pagination: {
    page: 1,
    totalPages: 1,
    next: '',
  },
};

const globalTaskSearch = async (
  state: RootState,
  count: boolean,
  page: number,
  apiRequestName?: OhsApiRequestName,
  extraFilters?: any
): Promise<ApiResponseType<ApiResListsType<TaskListsRecord[]>> | null> => {
  const {
    globalSearch: { searchInfo },
  } = state;
  const updateModules = [...searchInfo.filter.modules];
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;

  if (userTier !== 5) {
    updateModules.push(ModuleType.Task);
  }
  const requiredSearchFilters =
    apiRequestName === OhsApiRequestName.WorkerTaskAssignedList
      ? {
          page: searchInfo.filter?.page,
          count: searchInfo.filter?.count,
          modules: updateModules,
        }
      : { ...searchInfo.filter, modules: updateModules, count, page };

  const searchFilterInfo = {
    ...searchInfo,
    filter: { ...requiredSearchFilters, ...extraFilters },
  };

  const globalSearchRes: any = await globalModuleSearch(searchFilterInfo, apiRequestName);
  return globalSearchRes;
};

const taskListsRPC = async ({
  payload,
  hasSearch = false,
  taskInfo,
}: {
  payload: FetchTaskListTypes | OhsTaskSearchMain | OhsTaskListsFilterPayload;
  hasSearch: boolean;
  taskInfo?: FetchTaskListTypes;
}): Promise<any> => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;
  const isMyHistoryTab = taskInfo?.tabKey === OhsTabKeyTypes.MyHistory;

  // T5 & My History
  if (userTier === 5 && isMyHistoryTab) {
    return hasSearch
      ? getSignedOffTaskSearchList(payload as OhsTaskSearchMain)
      : getSignedOffTaskList(payload as OhsTaskListsFilterPayload);
  }
  // Regular List
  return hasSearch
    ? getTaskSearchLists(payload as OhsTaskSearchMain)
    : getTaskLists(taskInfo as any, payload as OhsTaskListsFilterPayload);
};

const taskListsFilterOptions = (state: RootState): Partial<OhsTaskListsFilterPayload> => {
  const { globalfilter, tasks } = state;
  const { filterInfo } = globalfilter;
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 100;
  const page = tasks.currentPage ?? 1;
  return {
    ...(userTier <= 3 && { skipOrgRecords: filterInfo.taskModule.skipOrgRecords }),
    modules: filterInfo.taskModule.modules ?? [],
    workplaces: filterInfo.taskModule.workplaces ?? [],
    next: page > 10 ? tasks.taskLists?.pagination.next ?? '' : undefined,
    // remove page if it is greater than 10
    ...(page <= 10 && { page }),
    dateDue: filterInfo.taskModule.dateDue,
  };
};

export const fetchTaskListsAsync = createAsyncThunk<
  ApiResponseType<ApiResListsType<TaskListsRecord[]>> | null,
  FetchTaskListTypes,
  { state: RootState }
>('tasks/fetchTaskList', async (taskInfo: FetchTaskListTypes, thunkAPI) => {
  const state = thunkAPI.getState();
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const hasGlobalSearch = state.globalSearch.searchInfo.searchKey !== '';
  const userTier = user?.tierNum ?? 0;
  const { currentPage } = state.tasks;
  const filter = state.globalfilter.filterInfo.taskModule;
  const updateTaskFilter: OhsTaskListsFilterPayload = {
    ...taskListsFilterOptions(state),
    // remove page if it is greater than 10
    ...(currentPage <= 10 && { page: currentPage }),
    sort: { ...JSON.parse(String(filter.sort)) },
    count: false,
  };
  try {
    const requiredApirequest =
      userTier === 5 ? OhsApiRequestName.WorkerTaskAssignedList : OhsApiRequestName.List;

    let response;

    const isMyHistoryTab = taskInfo.tabKey === OhsTabKeyTypes.MyHistory;
    if (userTier === 5 && isMyHistoryTab) {
      const { searchInfo } = state.globalSearch;
      const searchPayload = {
        ...searchInfo,
        filter: {
          page: searchInfo.filter.page,
          count: searchInfo.filter.count,
          modules: searchInfo.filter.modules,
          skipOrgRecords: searchInfo.filter.skipOrgRecords,
        },
      };
      response = await (hasGlobalSearch
        ? getSignedOffTaskSearchList(searchPayload as any)
        : getSignedOffTaskList(updateTaskFilter));
    } else {
      response = await (hasGlobalSearch
        ? globalTaskSearch(state, false, currentPage, requiredApirequest)
        : getTaskLists(taskInfo, updateTaskFilter));
    }

    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchTaskListsCountAsync = createAsyncThunk<
  ApiResponseType<ApiResListsType<TaskListsRecord[]>> | null,
  FetchTaskListTypes,
  { state: RootState }
>('tasks/fetchTaskListCount', async (taskInfo: FetchTaskListTypes, thunkAPI) => {
  const state = thunkAPI.getState();
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const hasGlobalSearch = state.globalSearch.searchInfo.searchKey !== '';
  const userTier = user?.tierNum ?? 0;
  const filter = state.globalfilter.filterInfo.taskModule;
  const taskFiltersWithCount: OhsTaskListsFilterPayload = {
    ...taskListsFilterOptions(state),
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filter.sort)) },
  };
  try {
    const requiredApirequest =
      userTier === 5 ? OhsApiRequestName.WorkerTaskAssignedList : OhsApiRequestName.List;

    let response;

    const isMyHistoryTab = taskInfo.tabKey === OhsTabKeyTypes.MyHistory;
    if (userTier === 5 && isMyHistoryTab) {
      const { searchInfo } = state.globalSearch;
      const searchPayload = {
        ...searchInfo,
        filter: {
          page: searchInfo.filter.page,
          count: searchInfo.filter.count,
          modules: searchInfo.filter.modules,
          skipOrgRecords: searchInfo.filter.skipOrgRecords,
        },
      };
      response = await (hasGlobalSearch
        ? getSignedOffTaskSearchList(searchPayload as any)
        : getSignedOffTaskList(taskFiltersWithCount));
    } else {
      response = await (hasGlobalSearch
        ? globalTaskSearch(state, true, 1, requiredApirequest)
        : getTaskLists(taskInfo, taskFiltersWithCount));
    }

    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchFlaggedTaskListsAsync = createAsyncThunk<
  ApiResponseType<ApiResListsType<TaskListsRecord[]>> | null,
  FetchTaskListTypes,
  { state: RootState }
>('tasks/fetchFlaggedTaskList', async (taskInfo: FetchTaskListTypes, thunkAPI) => {
  const state = thunkAPI.getState();
  const filter = state.globalfilter.filterInfo.taskModule;
  const updateTaskFilter: OhsTaskListsFilterPayload = {
    ...taskListsFilterOptions(state),
    page: 1,
    sort: { ...JSON.parse(String(filter.sort)) },
    count: false,
  };

  try {
    const response = await getTaskLists(taskInfo, updateTaskFilter);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchTaskListAsync = createAsyncThunk<
  {
    list: ApiResponseType<ApiResListsType<TaskListsRecord[]>> | null;
    count: ApiResponseType<ApiResListsType<TaskListsRecord[]>> | null;
  },
  {
    list: OhsTaskListsFilterPayload | OhsTaskSearchMain;
    count: OhsTaskListsFilterPayload | OhsTaskSearchMain;
    taskInfo: FetchTaskListTypes;
    hasSearch: boolean;
    withPermission: boolean;
  },
  { state: RootState }
>('actions/fetchTaskListAsync', async (payload, thunkAPI) => {
  const getListData = (data: FetchTaskListTypes | any) =>
    taskListsRPC({
      payload: data,
      hasSearch: payload.hasSearch,
      taskInfo: payload.taskInfo,
    });

  try {
    const [list, count] = await Promise.all([
      getListData(payload.list),
      getListData(payload.count),
    ]);
    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data);
  }
});

export const fetchFlaggedTaskListByIdAsync = createAsyncThunk<
  ApiResponseType<any> | null,
  string[],
  { state: RootState }
>('tasks/fetchFlaggedTaskListByIDAsync', async (_ids: any[], thunkAPI) => {
  try {
    const response = await getBookmarkListByTask(_ids);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response);
  }
});

export const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setTaskCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTaskIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetTasksLists: (state) => {
      state.taskLists = defaultTaskLists;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskListAsync.pending, (state) => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        state.isLoading = true;
        state.taskLists = {
          items: [],
          pagination: state.taskLists?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchTaskListAsync.fulfilled, (state, action) => {
        if (action.payload.list?.result) {
          state.taskLists = action.payload.list?.result;
          state.isLoading = false;

          let setItemsLastId = '';
          const taskItems = [...state.taskLists.items];
          if (taskItems.length > 0) setItemsLastId = taskItems?.pop()?._id || '';

          state.taskLists.pagination.next = setItemsLastId;
        }
        if (action.payload.count && state.taskLists) {
          state.taskLists.pagination.totalPages =
            action.payload.count.result?.pagination.totalPages ?? 0;
        }
      })
      .addCase(fetchTaskListAsync.rejected, (state) => {
        state.taskLists = null;
      })

      .addCase(fetchFlaggedTaskListsAsync.fulfilled, (state, action) => {
        const flaggedTaskCountResult = action.payload?.result ?? defaultTaskList;

        if (action.payload && action.payload.success) {
          state.flaggedTaskLists = flaggedTaskCountResult;
        }
      })
      .addCase(fetchFlaggedTaskListByIdAsync.fulfilled, (state, action) => {
        if (action.payload && action.payload.success) {
          state.flaggedTaskListsByID = action.payload;
        }
      });
  },
});

const taskState = (state: RootState) => state.tasks;
// Memoized Selectors
export const getOhsTaskModuleState = createSelector([taskState], (tasks) => tasks);
export const { setTaskCurrentPage, resetTasksLists, setTaskIsLoading } = taskSlice.actions;
export const taskModuleReducer = taskSlice.reducer;
